import { useState } from "react";
import CalculatorLayout from "@/components/CalculatorLayout";
import { ResultCard } from "@/components/ResultCard";
import AgeGenderFields from "@/components/AgeGenderFields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { round, parseNum, formatDuration, parseTime, lbToKg } from "@/calculators/_helpers";

// ---------- Pace ----------
const RACES = [
  { name: "5K", km: 5 },
  { name: "10K", km: 10 },
  { name: "Half marathon", km: 21.0975 },
  { name: "Marathon", km: 42.195 },
];
export function PaceCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [dist, setDist] = useState("");
  const [distUnit, setDistUnit] = useState("km");
  const [timeStr, setTimeStr] = useState("");
  const [pace, setPace] = useState("");
  const d = parseNum(dist);
  const t = parseTime(timeStr);
  const pSec = parseTime(pace);
  const distKm = distUnit === "km" ? d : d * 1.60934;

  let paceSecPerKm = 0, totalSec = 0, distanceKm = distKm;
  if (d > 0 && t > 0) {
    paceSecPerKm = t / distKm;
    totalSec = t;
  } else if (d > 0 && pSec > 0) {
    paceSecPerKm = distUnit === "km" ? pSec : pSec / 1.60934;
    totalSec = paceSecPerKm * distKm;
  } else if (t > 0 && pSec > 0) {
    paceSecPerKm = distUnit === "km" ? pSec : pSec / 1.60934;
    distanceKm = t / paceSecPerKm;
  }

  const paceKm = paceSecPerKm > 0 ? formatDuration(paceSecPerKm) : "";
  const paceMi = paceSecPerKm > 0 ? formatDuration(paceSecPerKm * 1.60934) : "";

  return (
    <CalculatorLayout
      calc={calc}
      intro="Enter any two of distance, time and pace — we'll fill in the third plus race predictions."
      resultText={paceSecPerKm > 0 ? `Pace: ${paceKm} /km · ${paceMi} /mi.` : null}
      formula={<p>Pace = time ÷ distance. Race predictions apply your input pace linearly — real-world times drift slower over longer distances due to fatigue.</p>}
      faqs={[
        { q: "Why do race predictions look too fast?", a: "This calculator projects at a constant pace. Riegel's or Cameron's formulas give better predictions for long races." },
        { q: "How do I type time?", a: "Use MM:SS or HH:MM:SS (e.g. 25:30 or 1:15:00)." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Distance</Label>
            <div className="flex gap-2">
              <Input type="number" inputMode="decimal" data-testid="pace-dist" value={dist} onChange={(e) => setDist(e.target.value)} />
              <select value={distUnit} onChange={(e) => setDistUnit(e.target.value)} className="h-10 rounded-md border border-border bg-background px-2 text-sm">
                <option value="km">km</option><option value="mi">mi</option>
              </select>
            </div>
          </div>
          <div className="space-y-2"><Label>Time (HH:MM:SS)</Label>
            <Input data-testid="pace-time" placeholder="00:25:30" value={timeStr} onChange={(e) => setTimeStr(e.target.value)} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Pace per {distUnit} (MM:SS) — leave blank to compute</Label>
            <Input data-testid="pace-pace" placeholder="05:00" value={pace} onChange={(e) => setPace(e.target.value)} /></div>
        </div>
      </div>
      {paceSecPerKm > 0 && (
        <div className="mt-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <ResultCard label="Pace / km" value={paceKm} testId="pace-km" />
            <ResultCard label="Pace / mi" value={paceMi} />
            <div className="rounded-2xl border border-border p-6"><div className="text-xs uppercase text-muted-foreground">{totalSec > 0 ? "Time" : "Distance"}</div><div className="mt-2 text-2xl font-bold">{totalSec > 0 ? formatDuration(totalSec) : `${round(distanceKm, 2)} km`}</div></div>
          </div>
          <div className="rounded-2xl border border-border p-4">
            <h3 className="text-sm font-semibold mb-3">Predicted finish times</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              {RACES.map((r) => (
                <div key={r.name} className="rounded-lg bg-secondary p-3">
                  <div className="text-xs text-muted-foreground">{r.name}</div>
                  <div className="font-semibold mt-0.5">{formatDuration(paceSecPerKm * r.km)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Calories Burned ----------
const ACTIVITIES = [
  { name: "Walking (3 mph)", met: 3.3 }, { name: "Walking (4 mph)", met: 5.0 },
  { name: "Running (5 mph)", met: 8.3 }, { name: "Running (6 mph)", met: 9.8 },
  { name: "Running (8 mph)", met: 11.8 }, { name: "Cycling (leisure, 10 mph)", met: 6.8 },
  { name: "Cycling (moderate, 14 mph)", met: 10.0 }, { name: "Swimming (freestyle, moderate)", met: 8.3 },
  { name: "Weight lifting (moderate)", met: 3.5 }, { name: "Weight lifting (vigorous)", met: 6.0 },
  { name: "Yoga", met: 2.5 }, { name: "HIIT", met: 8.0 },
  { name: "Rowing (moderate)", met: 7.0 }, { name: "Elliptical", met: 5.0 },
  { name: "Basketball", met: 6.5 }, { name: "Soccer", met: 7.0 },
  { name: "Tennis (singles)", met: 7.3 }, { name: "Hiking", met: 6.0 },
  { name: "Dancing", met: 5.0 }, { name: "Jump rope", met: 12.3 },
];
export function CaloriesBurnedCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [act, setAct] = useState(ACTIVITIES[0].name);
  const [unit, setUnit] = useState("metric");
  const [w, setW] = useState("");
  const [dur, setDur] = useState("");
  const kg = unit === "metric" ? parseNum(w) : lbToKg(parseNum(w));
  const met = ACTIVITIES.find((a) => a.name === act)?.met || 0;
  const hours = parseNum(dur) / 60;
  const kcal = kg > 0 && met > 0 && hours > 0 ? met * kg * hours : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimate calories burned during exercise using the standard MET formula."
      resultText={kcal > 0 ? `Estimated calories burned: ${Math.round(kcal)} kcal.` : null}
      formula={<p>Calories = MET × body weight (kg) × duration (hours). MET values are drawn from the Compendium of Physical Activities.</p>}
      faqs={[
        { q: "Are wearable estimates accurate?", a: "They're usually within 15–30% for cardio and much worse for lifting. Use MET-based estimates as sanity checks." },
        { q: "Can I combine activities?", a: "Yes — calculate each separately and add them up." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="space-y-2"><Label>Activity</Label>
          <select data-testid="activity" value={act} onChange={(e) => setAct(e.target.value)} className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
            {ACTIVITIES.map((a) => <option key={a.name} value={a.name}>{a.name} (MET {a.met})</option>)}
          </select></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2"><Label>Unit</Label>
            <RadioGroup value={unit} onValueChange={setUnit} className="flex gap-2">
              {["metric", "imperial"].map((u) => (
                <div key={u} className="flex-1">
                  <RadioGroupItem value={u} id={`cbu-${u}`} className="peer sr-only" />
                  <Label htmlFor={`cbu-${u}`} className="flex h-10 cursor-pointer items-center justify-center rounded-full border border-border text-sm font-medium peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground peer-data-[state=checked]:border-primary">{u === "metric" ? "kg" : "lb"}</Label>
                </div>
              ))}
            </RadioGroup></div>
          <div className="space-y-2"><Label>Weight ({unit === "metric" ? "kg" : "lb"})</Label>
            <Input type="number" data-testid="cb-w" value={w} onChange={(e) => setW(e.target.value)} /></div>
        </div>
        <div className="space-y-2"><Label>Duration (minutes)</Label>
          <Input type="number" data-testid="cb-dur" value={dur} onChange={(e) => setDur(e.target.value)} placeholder="e.g. 45" /></div>
      </div>
      {kcal > 0 && <div className="mt-6"><ResultCard label="Calories burned" value={Math.round(kcal)} unit="kcal" tone="warning" testId="cb-result" /></div>}
    </CalculatorLayout>
  );
}

// ---------- One Rep Max ----------
const PCT_CHART = [
  { pct: 100, reps: 1 }, { pct: 95, reps: 2 }, { pct: 93, reps: 3 }, { pct: 90, reps: 4 },
  { pct: 87, reps: 5 }, { pct: 85, reps: 6 }, { pct: 83, reps: 7 }, { pct: 80, reps: 8 },
  { pct: 77, reps: 9 }, { pct: 75, reps: 10 }, { pct: 70, reps: 12 }, { pct: 65, reps: 15 },
];
export function OneRepMaxCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [w, setW] = useState("");
  const [reps, setReps] = useState("");
  const [unit, setUnit] = useState("kg");
  const wt = parseNum(w), r = parseNum(reps);
  const oneRm = wt > 0 && r > 0 ? wt * (1 + r / 30) : 0;
  return (
    <CalculatorLayout
      calc={calc}
      intro="Estimate your one-rep max from a lift and see the full %1RM rep chart."
      resultText={oneRm > 0 ? `Estimated 1RM: ${round(oneRm, 1)} ${unit}.` : null}
      formula={<p><strong>Epley formula:</strong> 1RM = weight × (1 + reps ÷ 30). Best accuracy for 2–10 reps. Ranges beyond 12 reps get less reliable.</p>}
      faqs={[
        { q: "Should I actually test a 1RM?", a: "Only occasionally, with warm-up, spotter and good form. The estimate is safer for programming." },
        { q: "Which formula is best?", a: "Epley, Brzycki and Lombardi all agree within a couple of percent for typical rep ranges." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2"><Label>Weight lifted</Label>
            <Input type="number" data-testid="orm-w" value={w} onChange={(e) => setW(e.target.value)} /></div>
          <div className="space-y-2"><Label>Reps completed</Label>
            <Input type="number" data-testid="orm-r" value={reps} onChange={(e) => setReps(e.target.value)} /></div>
          <div className="space-y-2"><Label>Unit</Label>
            <select value={unit} onChange={(e) => setUnit(e.target.value)} className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
              <option value="kg">kg</option><option value="lb">lb</option>
            </select></div>
        </div>
      </div>
      {oneRm > 0 && (
        <div className="mt-6 space-y-4">
          <ResultCard label="Estimated 1RM" value={round(oneRm, 1)} unit={unit} testId="orm-result" />
          <div className="rounded-2xl border border-border overflow-hidden">
            <table className="w-full text-sm" data-testid="orm-chart">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr><th className="p-2 text-left">% 1RM</th><th className="p-2 text-right">Weight</th><th className="p-2 text-right">Reps</th></tr>
              </thead>
              <tbody>
                {PCT_CHART.map((row) => (
                  <tr key={row.pct} className="border-t border-border">
                    <td className="p-2">{row.pct}%</td>
                    <td className="p-2 text-right font-medium">{round((oneRm * row.pct) / 100, 1)} {unit}</td>
                    <td className="p-2 text-right">{row.reps}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}

// ---------- Target Heart Rate (Karvonen) ----------
export function TargetHeartRateCalculator({ calc }) {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [rest, setRest] = useState("");
  const a = parseNum(age), rhr = parseNum(rest);
  const maxHr = a > 0 ? 220 - a : 0;
  const hrr = maxHr > 0 && rhr > 0 ? maxHr - rhr : 0;
  const zone = (loPct, hiPct) => hrr > 0 ? [Math.round(loPct * hrr + rhr), Math.round(hiPct * hrr + rhr)] : [0, 0];
  const zones = [
    { label: "Recovery (50–60%)", range: zone(0.5, 0.6), tone: "good" },
    { label: "Fat burn (60–70%)", range: zone(0.6, 0.7), tone: "warning" },
    { label: "Aerobic (70–80%)", range: zone(0.7, 0.8) },
    { label: "Anaerobic (80–90%)", range: zone(0.8, 0.9) },
    { label: "Peak (90–100%)", range: zone(0.9, 1.0), tone: "danger" },
  ];
  return (
    <CalculatorLayout
      calc={calc}
      intro="Karvonen heart-rate training zones from your age and resting heart rate."
      resultText={hrr > 0 ? `Max HR ${maxHr} · HRR ${hrr}. Fat-burn ${zones[1].range[0]}–${zones[1].range[1]} bpm.` : null}
      formula={<p><strong>Karvonen:</strong> Target HR = intensity % × (max HR − resting HR) + resting HR. Max HR = 220 − age (rough estimate).</p>}
      faqs={[
        { q: "How do I measure resting HR?", a: "Count your pulse for 30 seconds first thing in the morning before getting out of bed, then double it." },
        { q: "Are these zones exact?", a: "No — max HR varies ±10 bpm from the 220-age estimate. Use a lab test if you need precision." },
      ]}
    >
      <div className="space-y-4">
        <AgeGenderFields age={age} setAge={setAge} gender={gender} setGender={setGender} />
        <div className="space-y-2"><Label>Resting heart rate (bpm)</Label>
          <Input type="number" data-testid="hr-rest" value={rest} onChange={(e) => setRest(e.target.value)} placeholder="e.g. 60" /></div>
      </div>
      {hrr > 0 && (
        <div className="mt-6 space-y-4" data-testid="hr-zones">
          <div className="grid gap-3 sm:grid-cols-2">
            <ResultCard label="Max HR" value={maxHr} unit="bpm" />
            <ResultCard label="Heart-rate reserve" value={hrr} unit="bpm" tone="warning" />
          </div>
          <div className="rounded-2xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr><th className="p-2 text-left">Zone</th><th className="p-2 text-right">Heart rate (bpm)</th></tr>
              </thead>
              <tbody>
                {zones.map((z, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-2">{z.label}</td>
                    <td className="p-2 text-right font-medium">{z.range[0]} – {z.range[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </CalculatorLayout>
  );
}
